/* Offline-capable calculator engine — JS port of backend/calculators.py.
   All formulas match backend to ~1e-6. */

const STEEL_E_PSI = 30_000_000;
const STEEL_POISSON = 0.3;
const STEEL_ALPHA_F = 6.9e-6;
const STEEL_ALPHA_C = 12.4e-6;

const round = (n, d = 2) => (Number.isFinite(n) ? +n.toFixed(d) : n);

// ---------- Hydrostatic ----------
function hydrostatic_pressure(i, u) {
  const mw = +i.mw, tvd = +i.tvd;
  if (u === "field") return { pressure: round(mw * 0.052 * tvd, 2), unit: "psi" };
  return { pressure: round((mw * 9.81 * tvd) / 100000, 2), unit: "bar" };
}

// ---------- Kill Mud Weight ----------
function kill_mud_weight(i, u) {
  const sidpp = +i.sidpp, mw = +i.current_mw, tvd = +i.tvd, sf = +i.safety_factor || 0;
  if (u === "field")
    return { kmw: round(sidpp / (0.052 * tvd) + mw + sf, 2), unit: "ppg" };
  return { kmw: round((sidpp * 100000) / (9.81 * tvd) + mw + sf, 1), unit: "kg/m³" };
}

// ---------- ICP / FCP ----------
function icp(i) {
  return { icp: round(+i.sidpp + +i.scr, 2), unit: "psi" };
}
function fcp(i) {
  if (+i.current_mw <= 0) return { fcp: 0, unit: "psi", error: "current MW must be > 0" };
  return { fcp: round(+i.scr * (+i.kmw / +i.current_mw), 2), unit: "psi" };
}

// ---------- MAASP ----------
function maasp(i) {
  const m = (+i.lot_ppg - +i.current_mw) * 0.052 * +i.shoe_tvd;
  return {
    maasp: round(m, 2),
    unit: "psi",
    warning: m < 0 ? "Negative MAASP: current mud weight exceeds fracture gradient at shoe." : null,
  };
}

// ---------- Formation Pressure ----------
function formation_pressure(i) {
  const tvd = +i.tvd;
  const fp = +i.sidpp + +i.mw * 0.052 * tvd;
  return { fp_psi: round(fp, 2), fp_ppg: tvd > 0 ? round(fp / (0.052 * tvd), 2) : 0, unit: "psi / ppg" };
}

function formation_strength(i) {
  const shoe = +i.shoe_tvd;
  if (shoe <= 0) return { error: "TVD must be > 0" };
  const fg = (+i.lot_surface + +i.mw * 0.052 * shoe) / (0.052 * shoe);
  return { fg_ppg: round(fg, 2), unit: "ppg" };
}

// ---------- Capacities ----------
function pipe_capacity(i, u) {
  const id = +i.id;
  if (u === "field") return { capacity: round((id * id) / 1029.4, 5), unit: "bbl/ft" };
  return { capacity: round((Math.PI / 4) * (id * id) / 1000, 4), unit: "L/m" };
}

function annular_capacity(i, u) {
  const D = +i.hole_id, d = +i.pipe_od;
  if (u === "field") return { capacity: round((D * D - d * d) / 1029.4, 5), unit: "bbl/ft" };
  return { capacity: round((Math.PI / 4) * (D * D - d * d) / 1000, 4), unit: "L/m" };
}

function volume(i) {
  return { volume: round(+i.capacity * +i.length, 3) };
}

function strokes(i) {
  if (+i.pump_output <= 0) return { error: "pump output must be > 0" };
  return { strokes: Math.ceil(+i.volume / +i.pump_output) };
}

// ---------- Tubing Movement ----------
function hookes_law(i) {
  const F = +i.force, L = +i.length, A = +i.area;
  return { stretch_in: round((F * L * 12) / (A * STEEL_E_PSI), 4), unit: "in" };
}

function piston_effect(i) {
  const L = +i.length * 12;
  const As = +i.as_;
  if (As <= 0) return { error: "steel cross-section area must be > 0" };
  const dl = (-L / (STEEL_E_PSI * As)) * (+i.delta_pi * (+i.ai - As) - +i.delta_po * (+i.ao - As));
  return { length_change_in: round(dl, 4), unit: "in", direction: dl < 0 ? "shortening" : "elongation" };
}

function ballooning(i) {
  const L = +i.length * 12;
  const denom = +i.ao - +i.ai;
  if (denom <= 0) return { error: "Ao must be greater than Ai" };
  const dl = ((-2 * STEEL_POISSON * L) / STEEL_E_PSI) * (+i.delta_pi_avg * +i.ai - +i.delta_po_avg * +i.ao) / denom;
  return { length_change_in: round(dl, 4), unit: "in", direction: dl < 0 ? "shortening" : "elongation" };
}

function thermal(i, u) {
  if (u === "field") {
    const dl = STEEL_ALPHA_F * +i.length * +i.delta_temp;
    return { length_change_in: round(dl * 12, 4), unit: "in" };
  }
  return { length_change_m: round(STEEL_ALPHA_C * +i.length * +i.delta_temp, 5), unit: "m" };
}

// ---------- Free Point ----------
function free_point(i) {
  const pull = +i.pull_diff, wt = +i.pipe_wt;
  if (pull <= 0 || wt <= 0) return { error: "pull diff and pipe weight must be > 0" };
  return { free_point_ft: round((+i.stretch * 735294 * wt) / pull, 1), unit: "ft" };
}

// ---------- Collapse / Burst ----------
function burst_barlow(i) {
  const sf = +i.safety || 0.875;
  return { burst_psi: Math.round((2 * +i.yield_strength * +i.wall_t * sf) / +i.od), unit: "psi" };
}

function collapse_yield(i) {
  const t = +i.wall_t;
  if (t <= 0) return { error: "wall thickness must be > 0" };
  const dt = +i.od / t;
  const p = (2 * +i.yield_strength * (dt - 1)) / (dt * dt);
  return { collapse_psi: Math.round(p), d_over_t: round(dt, 2), unit: "psi" };
}

// ---------- Mud weight ----------
function weight_up(i) {
  const w2 = +i.w2;
  if (35 - w2 <= 0) return { error: "target MW too high" };
  const sacksPerBbl = (14.7 * (w2 - +i.w1)) / (35 - w2);
  const sacks = sacksPerBbl * +i.v1;
  const newVol = +i.v1 * (1 + (sacksPerBbl * 100) / 3500);
  return { barite_sacks: round(sacks, 1), new_volume_bbl: round(newVol, 2) };
}

function weight_down(i) {
  const w1 = +i.w1, w2 = +i.w2, wD = +i.dilution_w || 8.33;
  if (w2 - wD <= 0) return { error: "target MW must exceed dilution fluid weight" };
  const add = (+i.v1 * (w1 - w2)) / (w2 - wD);
  return { dilution_volume_bbl: round(add, 2), final_volume_bbl: round(+i.v1 + add, 2) };
}

// ---------- Directional ----------
const rad = (d) => (d * Math.PI) / 180;
const deg = (r) => (r * 180) / Math.PI;

function _dogleg(i) {
  const c = Math.cos(rad(+i.i1)) * Math.cos(rad(+i.i2)) +
    Math.sin(rad(+i.i1)) * Math.sin(rad(+i.i2)) * Math.cos(rad(+i.a2 - +i.a1));
  return Math.acos(Math.max(-1, Math.min(1, c)));
}

function dogleg_severity(i) {
  if (+i.cl <= 0) return { error: "course length must be > 0" };
  const b = _dogleg(i);
  return {
    dogleg_deg: round(deg(b), 4),
    dls_deg_per_100ft: round((deg(b) * 100) / +i.cl, 3),
    unit: "°/100ft",
  };
}

function min_curvature(i) {
  if (+i.cl <= 0) return { error: "course length must be > 0" };
  const b = _dogleg(i);
  const rf = b < 1e-9 ? 1 : (2 / b) * Math.tan(b / 2);
  const factor = (+i.cl / 2) * rf;
  const dTvd = factor * (Math.cos(rad(+i.i1)) + Math.cos(rad(+i.i2)));
  const dN = factor * (Math.sin(rad(+i.i1)) * Math.cos(rad(+i.a1)) + Math.sin(rad(+i.i2)) * Math.cos(rad(+i.a2)));
  const dE = factor * (Math.sin(rad(+i.i1)) * Math.sin(rad(+i.a1)) + Math.sin(rad(+i.i2)) * Math.sin(rad(+i.a2)));
  return {
    dogleg_deg: round(deg(b), 4),
    ratio_factor: round(rf, 6),
    delta_tvd_ft: round(dTvd, 2),
    delta_north_ft: round(dN, 2),
    delta_east_ft: round(dE, 2),
    dls_deg_per_100ft: round((deg(b) * 100) / +i.cl, 3),
  };
}

// ---------- Kick Tolerance ----------
function kick_tolerance(i) {
  const shoe = +i.shoe_tvd, well = +i.well_tvd, mw = +i.current_mw, kf = +i.kick_fluid || 1.5;
  const ann = +i.ann_cap;
  if (ann <= 0) return { error: "annular capacity must be > 0" };
  if (well <= shoe) return { error: "well TVD must exceed shoe TVD" };
  const delta = mw - kf;
  if (delta <= 0) return { error: "kick fluid density must be less than current mud weight" };
  const pFrac = +i.shoe_frac * 0.052 * shoe;
  const pForm = (mw + +i.kick_intensity) * 0.052 * well;
  const lhs = pForm - (well - shoe) * 0.052 * mw;
  const h = (pFrac - lhs) / (0.052 * delta);
  const kt = h * ann;
  const warnings = [];
  let verdict = "GO";
  if (h <= 0) { verdict = "STOP"; warnings.push("Zero or negative kick tolerance — do not proceed."); }
  else if (kt < 10) { verdict = "CAUTION"; warnings.push("Very low kick tolerance (<10 bbl). Consider casing point earlier."); }
  if (+i.kick_intensity <= 0) warnings.push("Kick intensity is zero — verify swab margin and mud weight window.");
  return {
    kick_tolerance_bbl: round(Math.max(kt, 0), 2),
    max_kick_height_ft: round(Math.max(h, 0), 1),
    frac_pressure_shoe_psi: round(pFrac, 1),
    formation_pressure_psi: round(pForm, 1),
    shoe_pressure_current_psi: round(mw * 0.052 * shoe, 1),
    verdict,
    warnings,
  };
}

// ---------- Fishing ----------
function pipe_body_yield(i) {
  // MYS = As * Yp. As from OD/ID.
  const od = +i.od, id = +i.id, yp = +i.yield_strength;
  const A = (Math.PI / 4) * (od * od - id * id);
  return { area_in2: round(A, 3), yield_strength_lbf: Math.round(A * yp), unit: "lbf" };
}

function safe_pull_limit(i) {
  // Safe pull = MYS * pull_factor - string_weight_buoyed
  const od = +i.od, id = +i.id, yp = +i.yield_strength;
  const A = (Math.PI / 4) * (od * od - id * id);
  const mys = A * yp;
  const factor = +i.pull_factor || 0.9;
  const usable = mys * factor;
  const buoy = 1 - (+i.mw || 0) / 65.5;
  const stringWt = (+i.pipe_weight_ppf || 0) * (+i.length_ft || 0) * buoy;
  const overpull = usable - stringWt;
  const warnings = [];
  if (overpull <= 0) warnings.push("Overpull is zero or negative — string can't be pulled with any factor.");
  return {
    mys_lbf: Math.round(mys),
    usable_pull_lbf: Math.round(usable),
    buoyed_string_weight_lbf: Math.round(stringWt),
    buoyancy_factor: round(buoy, 4),
    max_overpull_lbf: Math.round(overpull),
    warnings,
    unit: "lbf",
  };
}

function jar_impact(i) {
  // Above-jar stretch under jar setting, released via jar fire:
  // stretch_in = F_jar * L_above_jar * 12 / (A_steel * E)
  // Impact energy (ft·lb) ≈ F_jar * stretch_in / 12
  // Impact velocity v = sqrt(2 * F_jar * stretch_in / (m * 12)); m = pipe mass above jar
  const F = +i.jar_setting_lbf;
  const L = +i.length_above_jar_ft;
  const wt = +i.pipe_weight_ppf;
  const od = +i.od, id = +i.id;
  const A = (Math.PI / 4) * (od * od - id * id);
  if (A <= 0 || L <= 0 || F <= 0) return { error: "OD/ID, length above jar, and jar setting must be > 0" };
  const E = 30_000_000;
  const stretch_in = (F * L * 12) / (A * E);
  const energy_ftlb = (F * stretch_in) / 12;
  // Mass above jar (slugs)
  const mass_lb = wt * L; // lb (weight)
  const mass_slug = mass_lb / 32.174; // slugs
  // Impact velocity from strain energy: v = sqrt(2*E/m); E in ft·lb, m in slugs → v in ft/s
  const v = mass_slug > 0 ? Math.sqrt((2 * energy_ftlb) / mass_slug) : 0;
  // Peak impact force approx: 2x-5x of jar setting depending on stretch. Conservative 3x.
  const peak_force = F * 3;
  return {
    above_jar_stretch_in: round(stretch_in, 3),
    impact_energy_ft_lb: Math.round(energy_ftlb),
    impact_velocity_fps: round(v, 2),
    peak_impact_force_lbf: Math.round(peak_force),
    string_mass_lb: Math.round(mass_lb),
    unit: "mixed",
    warnings: energy_ftlb < 500 ? ["Impact energy < 500 ft·lb — jar setting or stretch is low, may not free stuck pipe."] : [],
  };
}

// ---------- Cementing ----------
function cement_slurry_volume(i) {
  // Vol = annular_cap * annulus_length + shoetrack (pipe cap) * shoetrack_length
  const av = +i.ann_cap * +i.annulus_length;
  const st = +i.shoetrack_cap * +i.shoetrack_length;
  const total = av + st;
  return {
    annulus_volume_bbl: round(av, 2),
    shoetrack_volume_bbl: round(st, 2),
    total_slurry_bbl: round(total, 2),
    total_slurry_ft3: round(total * 5.6146, 2),
    unit: "bbl",
  };
}

function cement_sacks(i) {
  // sacks = slurry_bbl * 5.6146 / yield_ft3_per_sack (default 1.15 ft³/sk Class G)
  const y = +i.yield_ft3_per_sack || 1.15;
  if (y <= 0) return { error: "yield must be > 0" };
  const sacks = (+i.slurry_bbl * 5.6146) / y;
  const water_gps = +i.water_gps_per_sack || 5.0;
  const mix_water_gal = sacks * water_gps;
  const mix_water_bbl = mix_water_gal / 42.0;
  return {
    sacks_of_cement: Math.round(sacks),
    mix_water_gal: Math.round(mix_water_gal),
    mix_water_bbl: round(mix_water_bbl, 2),
    unit: "sk / gal / bbl",
  };
}

function cement_displacement(i) {
  // Displacement = pipe_cap * (well_depth - float_collar_depth)
  const dv = +i.pipe_cap * (+i.well_depth - +i.float_collar_depth);
  if (dv < 0) return { error: "float collar must be above well depth" };
  const strokes = +i.pump_output > 0 ? Math.ceil(dv / +i.pump_output) : null;
  return {
    displacement_bbl: round(dv, 2),
    strokes: strokes,
    unit: "bbl / stk",
  };
}

function cement_slurry_density(i) {
  // Density (ppg) from cement, water, additives:
  // Basic Portland Class G: 15.6 ppg at 5.0 gps mix. Formula:
  // density_ppg = (94 + 8.33*water_gps + additive_mass) / (94/SG_cement + water_gps + additive_vol) / <conversion>
  // We use user-supplied cement SG (default 3.14 for Class G) and mix water gal/sk.
  // Slurry weight per sack = 94 (cement) + 8.33 * water_gps (water lb) + additive_lb_per_sk
  // Slurry vol per sack (gal) = 94 / (SG*8.33) + water_gps + additive_gal_per_sk
  // density (ppg) = weight / vol
  const cement_sg = +i.cement_sg || 3.14;
  const water_gps = +i.water_gps_per_sack || 5.0;
  const additive_lb = +i.additive_lb_per_sack || 0;
  const additive_gal = +i.additive_gal_per_sack || 0;
  const wt = 94 + 8.33 * water_gps + additive_lb;
  const vol = 94 / (cement_sg * 8.33) + water_gps + additive_gal;
  if (vol <= 0) return { error: "slurry volume must be > 0" };
  return {
    slurry_density_ppg: round(wt / vol, 2),
    slurry_yield_ft3_per_sack: round(vol / 7.4805, 3),
    weight_per_sack_lb: round(wt, 1),
    unit: "ppg",
  };
}

// ---------- Torque & Drag (Soft-string) ----------
function torque_drag(i) {
  const sections = i.sections || [];
  if (!sections.length) return { error: "At least one section required" };
  const mw = +i.mw;
  const wt = +i.wt_ppf;
  const od = +i.od_in;
  const buoy = 1 - mw / 65.5;
  const r_ft = od / 2 / 12;
  let f_axial_rih = 0, f_axial_pooh = 0, f_axial_rot = 0;
  let total_torque = 0;
  const profile = [];
  for (let k = sections.length - 1; k >= 0; k--) {
    const s = sections[k];
    const L = +s.length_ft;
    const alpha = (+s.inclination_deg * Math.PI) / 180;
    const mu = +s.friction_factor;
    const w_buoy = wt * L * buoy;
    const axial = w_buoy * Math.cos(alpha);
    const normal = Math.abs(w_buoy * Math.sin(alpha));
    const drag = mu * normal;
    f_axial_rih += axial - drag;
    f_axial_pooh += axial + drag;
    f_axial_rot += axial;
    total_torque += mu * normal * r_ft;
    profile.push({
      section: sections.length - k,
      length_ft: L,
      inclination_deg: +s.inclination_deg,
      buoyed_weight_lbf: Math.round(w_buoy),
      normal_force_lbf: Math.round(normal),
      section_drag_lbf: Math.round(drag),
    });
  }
  return {
    hookload_rih_lbf: Math.round(Math.max(f_axial_rih, 0)),
    hookload_pooh_lbf: Math.round(f_axial_pooh),
    hookload_rotating_lbf: Math.round(f_axial_rot),
    total_torque_ftlb: Math.round(total_torque),
    buoyancy_factor: round(buoy, 4),
    profile,
    unit: "lbf / ft-lb",
  };
}


// ---------- Registry ----------
const REGISTRY = {
  hydrostatic_pressure,
  kill_mud_weight,
  icp,
  fcp,
  maasp,
  formation_pressure,
  formation_strength,
  pipe_capacity,
  annular_capacity,
  volume,
  strokes,
  hookes_law,
  piston_effect,
  ballooning,
  thermal,
  free_point,
  burst_barlow,
  collapse_yield,
  weight_up,
  weight_down,
  dogleg_severity,
  min_curvature,
  kick_tolerance,
  pipe_body_yield,
  safe_pull_limit,
  jar_impact,
  cement_slurry_volume,
  cement_sacks,
  cement_displacement,
  cement_slurry_density,
  torque_drag,
};

export function localCalc(calculator, inputs, units = "field") {
  const fn = REGISTRY[calculator];
  if (!fn) throw new Error(`Unknown calculator: ${calculator}`);
  return fn(inputs, units);
}

export const OFFLINE_CALCS = Object.keys(REGISTRY);
